import { useCallback } from 'react'
import { useSession } from '../../auth/useSession'
import { LocationMap } from '../../map/LocationMap'
import { alertReportSites } from '../reportsAccess'
import { reportLoading } from '../ui'
import { AlertsReport } from './AlertsReport'
import { AlertTripDrawer } from './AlertTripDrawer'
import type { LocationsMapSlot } from './views/viewKit'

/**
 * Reports > Alerts, wired to the session and to the map pieces. `AlertsReport` itself is
 * deliberately session-free (its tests drive it with explicit `sites`); this is the one
 * place that decides, from the signed-in account:
 *
 *  - which site pills the filter bar shows (`alertReportSites`), and therefore which site
 *    a bare `/reports/alerts` opens on and what a disallowed `?site=` is coerced to;
 *  - the Locations tab's map: a bubble per ≈1 km bin, click = list that hotspot's alerts
 *    (each one opens the trip drawer);
 *  - the alert drawer for `?alert=<id>`: the trip the alert happened on. Picking another
 *    alert inside the drawer highlights it locally only -- it is the same trip, so
 *    writing it to `?alert=` would just refetch identical data; the URL keeps the alert
 *    the reader opened, which is the one a shared link should land on.
 *
 * While /api/me is still answering nothing is fetched: a Wonder-Cement-only account must
 * not fire an Ultratech request (and collect a 403 toast) in the gap before its sites are
 * known.
 */
export function ConnectedAlertsReport(): JSX.Element {
  const session = useSession()

  const locationsMap: LocationsMapSlot = useCallback((rows, { filters, setFilters, selectBin }) => (
    <LocationMap
      rows={rows}
      onSelect={(row) => (selectBin ? selectBin(row) : setFilters({ hub: filters.hub === row.place ? '' : row.place }))}
    />
  ), [])

  const alertDrawer = useCallback((alertId: string, onClose: () => void) => (
    <AlertTripDrawer alertId={alertId} onClose={onClose} />
  ), [])

  if (session.status !== 'signed-in') {
    return <div className={reportLoading} aria-busy="true" aria-live="polite">Checking access…</div>
  }

  return (
    <AlertsReport
      sites={alertReportSites(session.session)}
      locationsMap={locationsMap}
      alertDrawer={alertDrawer}
    />
  )
}
